/**
 * DXF (ASCII) → GeoJSON FeatureCollection for the object import pipeline.
 * Emits properties.Layer / properties.Text compatible with ogr2ogr-style GeoJSON CAD imports.
 *
 * Supported entities: LINE, LWPOLYLINE, POLYLINE(+VERTEX), POINT, CIRCLE (center Point),
 * TEXT, MTEXT. INSERT / HATCH / IMAGE and unknown types are skipped.
 */

export interface DxfGeoJsonFeature {
  type: 'Feature';
  geometry: {
    type: 'Point' | 'LineString' | 'Polygon' | 'MultiLineString';
    coordinates: number[] | number[][] | number[][][];
  };
  properties: Record<string, unknown>;
}

export interface DxfGeoJsonFeatureCollection {
  type: 'FeatureCollection';
  features: DxfGeoJsonFeature[];
}

export interface DxfToGeoJsonResult {
  featureCollection: DxfGeoJsonFeatureCollection;
  stats: {
    entityCount: number;
    featureCount: number;
    skippedEntityTypes: Record<string, number>;
  };
}

type DxfPair = { code: number; value: string };

function parseDxfPairs(text: string): DxfPair[] {
  const normalized = String(text ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n');
  const pairs: DxfPair[] = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = Number.parseInt(lines[i].trim(), 10);
    if (!Number.isFinite(code)) continue;
    pairs.push({ code, value: lines[i + 1] ?? '' });
  }
  return pairs;
}

function num(value: string): number | null {
  const n = Number.parseFloat(String(value).trim());
  return Number.isFinite(n) ? n : null;
}

/** Strip common MTEXT formatting wrappers (`{\fArial|b0;text}` → `text`). */
export function stripDxfMtextFormatting(raw: string): string {
  let s = String(raw ?? '');
  s = s.replace(/\\P/gi, '\n');
  s = s.replace(/\\~|\\ /g, ' ');
  // Nested {\f...;content} or {\F...;content}
  for (let i = 0; i < 8; i++) {
    const next = s.replace(/\{\\[fF][^;]*;([^{}]*)\}/g, '$1');
    if (next === s) break;
    s = next;
  }
  s = s.replace(/\{\\H[^;]*;([^{}]*)\}/g, '$1');
  s = s.replace(/\\[A-Za-z][^;\\]*;?/g, '');
  s = s.replace(/[{}]/g, '');
  return s.replace(/\s+/g, ' ').trim();
}

function entityProps(layer: string, extra?: Record<string, unknown>): Record<string, unknown> {
  return {
    Layer: layer || '0',
    ...(extra ?? {}),
  };
}

function closeRing(pts: number[][]): number[][] {
  if (pts.length < 3) return pts;
  const [fx, fy] = pts[0];
  const [lx, ly] = pts[pts.length - 1];
  if (fx === lx && fy === ly) return pts;
  return [...pts, [fx, fy]];
}

type MutableEntity = {
  type: string;
  layer: string;
  pairs: DxfPair[];
};

function collectEntities(pairs: DxfPair[]): MutableEntity[] {
  const entities: MutableEntity[] = [];
  let inEntities = false;
  let current: MutableEntity | null = null;

  const flush = () => {
    if (current) {
      entities.push(current);
      current = null;
    }
  };

  for (let i = 0; i < pairs.length; i++) {
    const { code, value } = pairs[i];
    const trimmed = value.trim();

    if (code === 0 && trimmed === 'SECTION') {
      const namePair = pairs[i + 1];
      if (namePair?.code === 2 && namePair.value.trim() === 'ENTITIES') {
        inEntities = true;
      }
      continue;
    }
    if (code === 0 && trimmed === 'ENDSEC' && inEntities) {
      flush();
      inEntities = false;
      continue;
    }
    if (!inEntities) continue;

    if (code === 0) {
      flush();
      if (trimmed === 'ENDSEC' || trimmed === 'EOF') continue;
      current = { type: trimmed, layer: '0', pairs: [] };
      continue;
    }
    if (!current) continue;
    if (code === 8) current.layer = trimmed || '0';
    current.pairs.push({ code, value });
  }
  flush();
  return entities;
}

function featureFromLine(layer: string, pairs: DxfPair[]): DxfGeoJsonFeature | null {
  let x1: number | null = null;
  let y1: number | null = null;
  let x2: number | null = null;
  let y2: number | null = null;
  for (const { code, value } of pairs) {
    if (code === 10) x1 = num(value);
    else if (code === 20) y1 = num(value);
    else if (code === 11) x2 = num(value);
    else if (code === 21) y2 = num(value);
  }
  if (x1 == null || y1 == null || x2 == null || y2 == null) return null;
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: [[x1, y1], [x2, y2]] },
    properties: entityProps(layer),
  };
}

function featureFromLwPolyline(layer: string, pairs: DxfPair[]): DxfGeoJsonFeature | null {
  const pts: number[][] = [];
  let pendingX: number | null = null;
  let closed = false;
  for (const { code, value } of pairs) {
    if (code === 70) {
      const flags = Number.parseInt(value.trim(), 10) || 0;
      closed = (flags & 1) === 1;
    } else if (code === 10) {
      pendingX = num(value);
    } else if (code === 20 && pendingX != null) {
      const y = num(value);
      if (y != null) pts.push([pendingX, y]);
      pendingX = null;
    }
  }
  if (pts.length < 2) {
    if (pts.length === 1) {
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: pts[0] },
        properties: entityProps(layer),
      };
    }
    return null;
  }
  if (closed && pts.length >= 3) {
    return {
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [closeRing(pts)] },
      properties: entityProps(layer),
    };
  }
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: closed ? closeRing(pts) : pts },
    properties: entityProps(layer),
  };
}

function featureFromPointLike(
  layer: string,
  pairs: DxfPair[],
  text?: string,
): DxfGeoJsonFeature | null {
  let x: number | null = null;
  let y: number | null = null;
  for (const { code, value } of pairs) {
    if (code === 10) x = num(value);
    else if (code === 20) y = num(value);
  }
  if (x == null || y == null) return null;
  const properties = entityProps(layer, text != null && text !== '' ? { Text: text } : undefined);
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [x, y] },
    properties,
  };
}

function textFromPairs(pairs: DxfPair[], mtext: boolean): string {
  const chunks: string[] = [];
  for (const { code, value } of pairs) {
    if (code === 1 || (mtext && code === 3)) chunks.push(value);
  }
  const joined = chunks.join('');
  return mtext ? stripDxfMtextFormatting(joined) : joined.trim();
}

/**
 * Convert ASCII DXF text to a FeatureCollection.
 * Throws if the payload does not look like DXF (no SECTION/ENTITIES).
 */
export function dxfToGeoJsonFeatureCollection(dxfText: string): DxfToGeoJsonResult {
  const pairs = parseDxfPairs(dxfText);
  if (pairs.length < 4) {
    throw new Error('Neplatný DXF: prázdný nebo nečitelný obsah.');
  }
  const hasSection = pairs.some((p) => p.code === 0 && p.value.trim() === 'SECTION');
  if (!hasSection) {
    throw new Error('Neplatný DXF: chybí SECTION.');
  }

  const entities = collectEntities(pairs);
  const features: DxfGeoJsonFeature[] = [];
  const skippedEntityTypes: Record<string, number> = {};
  let openPoly: { layer: string; pts: number[][]; closed: boolean } | null = null;

  const bumpSkip = (type: string) => {
    skippedEntityTypes[type] = (skippedEntityTypes[type] ?? 0) + 1;
  };

  const flushPoly = () => {
    if (!openPoly) return;
    const { layer, pts, closed } = openPoly;
    openPoly = null;
    if (pts.length >= 2) {
      if (closed && pts.length >= 3) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [closeRing(pts)] },
          properties: entityProps(layer),
        });
      } else {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: closed ? closeRing(pts) : pts },
          properties: entityProps(layer),
        });
      }
    } else if (pts.length === 1) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: pts[0] },
        properties: entityProps(layer),
      });
    } else {
      bumpSkip('POLYLINE');
    }
  };

  for (const ent of entities) {
    const t = ent.type.toUpperCase();
    if (t === 'POLYLINE') {
      flushPoly();
      let closed = false;
      for (const { code, value } of ent.pairs) {
        if (code === 70) closed = ((Number.parseInt(value.trim(), 10) || 0) & 1) === 1;
      }
      openPoly = { layer: ent.layer, pts: [], closed };
      continue;
    }
    if (t === 'VERTEX' && openPoly) {
      let x: number | null = null;
      let y: number | null = null;
      for (const { code, value } of ent.pairs) {
        if (code === 10) x = num(value);
        else if (code === 20) y = num(value);
      }
      if (x != null && y != null) openPoly.pts.push([x, y]);
      continue;
    }
    if (t === 'SEQEND') {
      flushPoly();
      continue;
    }
    if (openPoly && t !== 'VERTEX') {
      flushPoly();
    }

    let feature: DxfGeoJsonFeature | null = null;
    switch (t) {
      case 'LINE':
        feature = featureFromLine(ent.layer, ent.pairs);
        break;
      case 'LWPOLYLINE':
        feature = featureFromLwPolyline(ent.layer, ent.pairs);
        break;
      case 'POINT':
      case 'CIRCLE':
        feature = featureFromPointLike(ent.layer, ent.pairs);
        break;
      case 'TEXT':
        feature = featureFromPointLike(ent.layer, ent.pairs, textFromPairs(ent.pairs, false));
        break;
      case 'MTEXT':
        feature = featureFromPointLike(ent.layer, ent.pairs, textFromPairs(ent.pairs, true));
        break;
      default:
        bumpSkip(t || 'UNKNOWN');
        break;
    }
    if (feature) features.push(feature);
    else if (t === 'LINE' || t === 'LWPOLYLINE' || t === 'POINT' || t === 'CIRCLE' || t === 'TEXT' || t === 'MTEXT') {
      bumpSkip(t);
    }
  }
  flushPoly();

  if (features.length === 0) {
    throw new Error('DXF neobsahuje podporované entity (LINE, LWPOLYLINE, TEXT, …).');
  }

  return {
    featureCollection: { type: 'FeatureCollection', features },
    stats: {
      entityCount: entities.length,
      featureCount: features.length,
      skippedEntityTypes,
    },
  };
}

/** True if filename looks like DXF. */
export function isDxfFileName(fileName: string | null | undefined): boolean {
  return String(fileName ?? '').toLowerCase().endsWith('.dxf');
}

/** True if filename looks like MicroStation DGN (not supported for import). */
export function isDgnFileName(fileName: string | null | undefined): boolean {
  return String(fileName ?? '').toLowerCase().endsWith('.dgn');
}
